import type { AssetCatalogEntry } from '@buxus/shared'
import {
  trackButtonPressed,
  trackMenuItemSelected,
  type ScreenInteractionContext,
} from '../../platform/analytics/interactionAnalytics'

type MapAssetOverlayRailProps = {
  entries: readonly AssetCatalogEntry[]
  totalCount: number
  open: boolean
  selectedAssetId: string | null
  hoverAssetId: string | null
  onOpenChange: (open: boolean) => void
  onPreviewHoverStart: (assetId: string) => void
  onPreviewHoverEnd: () => void
  onFocusAsset: (entry: AssetCatalogEntry) => void
  analytics?: ScreenInteractionContext
}

/** Translucent asset list rail over the map (All Assets map mode). */
export function MapAssetOverlayRail({
  entries,
  totalCount,
  open,
  selectedAssetId,
  hoverAssetId,
  onOpenChange,
  onPreviewHoverStart,
  onPreviewHoverEnd,
  onFocusAsset,
  analytics,
}: MapAssetOverlayRailProps) {
  if (totalCount === 0) return null

  const heading = `Assets (${totalCount})`

  if (!open) {
    return (
      <aside className="map-asset-rail map-asset-rail--collapsed" aria-label={heading}>
        <button
          type="button"
          className="map-asset-rail-handle"
          onClick={() => {
            if (analytics) {
              trackButtonPressed({ ...analytics, button: heading })
            }
            onOpenChange(true)
          }}
          aria-label={`Show ${heading}`}
          title={`Show ${heading}`}
        >
          <span className="map-asset-rail-handle-label">Assets ({totalCount})</span>
        </button>
      </aside>
    )
  }

  return (
    <aside className="map-asset-rail map-asset-rail--open" aria-label={heading}>
      <div className="map-asset-rail-header">
        <h2 className="map-asset-rail-title">{heading}</h2>
        <button
          type="button"
          className="map-asset-rail-hide"
          onClick={() => {
            if (analytics) {
              trackButtonPressed({ ...analytics, button: 'Hide' })
            }
            onOpenChange(false)
          }}
        >
          Hide
        </button>
      </div>
      <div className="map-asset-rail-scroll">
        <ul className="map-asset-rail-list">
          {entries.map((entry) => {
            const active = selectedAssetId === entry.id
            const hovered = hoverAssetId === entry.id
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  className={[
                    'map-asset-rail-item',
                    active ? 'active' : '',
                    hovered ? 'hovered' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseEnter={() => onPreviewHoverStart(entry.id)}
                  onMouseLeave={() => onPreviewHoverEnd()}
                  onClick={(ev) => {
                    ev.stopPropagation()
                    if (analytics) {
                      trackMenuItemSelected({
                        ...analytics,
                        component: 'MapAssetRail',
                        menu: 'Assets',
                        selection: entry.displayName,
                      })
                    }
                    onFocusAsset(entry)
                  }}
                >
                  <span className="map-asset-rail-item-title">{entry.displayName}</span>
                  {entry.countiesLabel ? (
                    <span className="map-asset-rail-item-meta">{entry.countiesLabel}</span>
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </aside>
  )
}
